import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Checkbox,
  Field,
  Input,
  SearchBox,
  Spinner,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  tokens,

  makeStyles,
  shorthands,
} from '@fluentui/react-components';
import { DeleteRegular } from '@fluentui/react-icons';
import type { Branding } from '@tvmf/shared';
import { api, apiUpload } from '../../api';
import { useAuth } from '../../auth';
import { DataTable } from '../../components/DataTable';
import { Page } from '../../components/Page';
import { Wordmark } from '../../components/Logo';
import { useDebounced } from '../../hooks/useDebounced';
import { LoadError } from '../DataCollection';
import { DangerText, MutedText } from '../../components/ui';
import { TableSkeleton } from '../../components/states';

const useLocalStyles = makeStyles({
  flexWrapItemsEndGap12: { display: "flex", ...shorthands.gap('12px'), flexWrap: "wrap", alignItems: "end" },
  w40H32FilledPad0: { width: '40px', height: '32px', ...shorthands.padding("0px"), ...shorthands.border("0"), backgroundColor: "none", cursor: "pointer" },
  maxW320: { maxWidth: '320px' },
  flexItemsCenterGap8: { display: "flex", alignItems: "center", ...shorthands.gap('8px') },
  blockWAutoH20: { height: '20px', width: "auto", display: "block" },
  danger: { color: tokens.colorPaletteRedForeground1 },
  blockMt8Danger: { color: tokens.colorPaletteRedForeground1, display: "block", marginTop: '8px' },
  dangerFill: { backgroundColor: tokens.colorPaletteRedBackground3 },
  gridGap12MinW380: { display: "grid", ...shorthands.gap('12px'), minWidth: '380px' },
  flexItemsCenterGap12: { display: "flex", alignItems: "center", ...shorthands.gap('12px') },
  wAutoH40: { height: '40px', width: "auto" },
  w94vwMaxW640: { maxWidth: "640px", width: "94vw" },
  flexColRowGap12: { display: "flex", flexDirection: "column", rowGap: '12px' },
});


const DEFAULT_ACCENT = '#4657D2';

interface Msp {
  id: string;
  name: string;
  slug: string;
  domains: string[];
  branding: Branding | null;
  created_at: string;
}

/** Splits a comma/newline-separated list of domains into a clean array - the server also
 *  lowercases/trims/dedupes, this is just to show the admin a sane preview before saving. */
function splitDomains(raw: string): string[] {
  return raw
    .split(/[,\n]/)
    .map((d) => d.trim().toLowerCase().replace(/^@/, ''))
    .filter(Boolean);
}

export function AdminMsps() {
  const ls = useLocalStyles();
  const qc = useQueryClient();
  const { can } = useAuth();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [domainsRaw, setDomainsRaw] = useState('');
  const [accentColor, setAccentColor] = useState(DEFAULT_ACCENT);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [brandingFor, setBrandingFor] = useState<Msp | null>(null);
  const [teamsFor, setTeamsFor] = useState<Msp | null>(null);
  const [editFor, setEditFor] = useState<Msp | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const search = useDebounced(searchInput);
  const list = useQuery({
    queryKey: ['msps', search],
    queryFn: () => api<Msp[]>(`/msps${search ? `?q=${encodeURIComponent(search)}` : ''}`),
  });
  const create = useMutation({
    mutationFn: async () => {
      const created = await api<Msp>('/msps', {
        method: 'POST',
        body: JSON.stringify({ name, slug, domains: splitDomains(domainsRaw) }),
      });
      if (accentColor !== DEFAULT_ACCENT) {
        await api(`/msps/${created.id}/branding`, { method: 'PATCH', body: JSON.stringify({ accentColor }) });
      }
      if (logoFile) await apiUpload(`/msps/${created.id}/branding/logo`, logoFile);
      return created;
    },
    onSuccess: () => {
      setName('');
      setSlug('');
      setDomainsRaw('');
      setAccentColor(DEFAULT_ACCENT);
      setLogoFile(null);
      setErr(null);
      qc.invalidateQueries({ queryKey: ['msps'] });
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Failed'),
  });

  return (
    <Page platform
      title="MSPs"
      subtitle="Managed Service Providers - engineers and project managers see their MSP's branding in the app chrome, resolved by their account's email domain."
    >
      <Card>
        <Text weight="semibold">New MSP</Text>
        <div className={ls.flexWrapItemsEndGap12}>
          <Field label="Name">
            <Input value={name} onChange={(_, d) => setName(d.value)} />
          </Field>
          <Field label="Slug" hint="lowercase, hyphens">
            <Input
              value={slug}
              onChange={(_, d) => setSlug(d.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            />
          </Field>
          <Field label="Domains" hint="comma or newline separated, e.g. contoso.com, intl.contoso.com">
            <Input value={domainsRaw} onChange={(_, d) => setDomainsRaw(d.value)} placeholder="contoso.com" />
          </Field>
          <Field label="Accent color">
            <input
              type="color"
              value={accentColor}
              onChange={(e) => setAccentColor(e.target.value)}
              className={ls.w40H32FilledPad0}
            />
          </Field>
          <Field label="Logo (optional)" hint="PNG or JPEG - WebP isn't supported by classic Outlook desktop">
            <input type="file" accept="image/png,image/jpeg" onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)} />
          </Field>
          <Button
            appearance="primary"
            disabled={!name || slug.length < 2 || create.isPending}
            onClick={() => create.mutate()}
          >
            Create
          </Button>
        </div>
        {err && <DangerText>{err}</DangerText>}
      </Card>

      <Card>
        <SearchBox
          size="small"
          placeholder="Search by name or slug…"
          value={searchInput}
          onChange={(_, d) => setSearchInput(d.value)}
          className={ls.maxW320}
        />
        {list.isLoading ? (
          <TableSkeleton rows={4} cols={5} />
        ) : list.isError ? (
          <LoadError message={(list.error as Error).message} />
        ) : (
          <DataTable size="small" minWidth={700}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Slug</TableHeaderCell>
                <TableHeaderCell>Domains</TableHeaderCell>
                <TableHeaderCell>Branding</TableHeaderCell>
                <TableHeaderCell>Teams channel</TableHeaderCell>
                <TableHeaderCell />
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.data!.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>{m.name}</TableCell>
                  <TableCell>{m.slug}</TableCell>
                  <TableCell>
                    <div className={ls.flexItemsCenterGap8}>
                      <span>{m.domains.length ? m.domains.join(', ') : '—'}</span>
                      {can('msp:update') && (
                        <Button size="small" appearance="subtle" onClick={() => setEditFor(m)}>
                          Edit
                        </Button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className={ls.flexItemsCenterGap8}>
                      {m.branding?.logo && (
                        <img
                          src={`/api/public/msps/${m.id}/logo?v=${m.branding.logo.version}`}
                          alt=""
                          className={ls.blockWAutoH20}
                        />
                      )}
                      {can('msp:update') ? (
                        <Button size="small" appearance="subtle" onClick={() => setBrandingFor(m)}>
                          Edit branding
                        </Button>
                      ) : (
                        m.branding && (
                          <MutedText size={200}>
                            Branded
                          </MutedText>
                        )
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {can('msp:update') && (
                      <Button size="small" appearance="subtle" onClick={() => setTeamsFor(m)}>
                        Teams channel
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    {can('msp:delete') && <DeleteMspButton msp={m} onDeleted={() => qc.invalidateQueries({ queryKey: ['msps'] })} />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </DataTable>
        )}
      </Card>

      {brandingFor && (
        <BrandingDialog
          msp={brandingFor}
          onClose={() => setBrandingFor(null)}
          onSaved={() => {
            setBrandingFor(null);
            qc.invalidateQueries({ queryKey: ['msps'] });
          }}
        />
      )}

      {teamsFor && <TeamsDialog msp={teamsFor} onClose={() => setTeamsFor(null)} />}

      {editFor && (
        <EditMspDialog
          msp={editFor}
          onClose={() => setEditFor(null)}
          onSaved={() => {
            setEditFor(null);
            qc.invalidateQueries({ queryKey: ['msps'] });
          }}
        />
      )}
    </Page>
  );
}

/* -------------------------------- delete --------------------------------- */

function DeleteMspButton({ msp, onDeleted }: { msp: Msp; onDeleted: () => void }) {
  const ls = useLocalStyles();
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const del = useMutation({
    mutationFn: () => api(`/msps/${msp.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setOpen(false);
      onDeleted();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Failed'),
  });

  return (
    <>
      <Button
        size="small"
        appearance="subtle"
        icon={<DeleteRegular />}
        className={ls.danger}
        title="Delete MSP"
        aria-label="Delete MSP"
        onClick={() => {
          setErr(null);
          setOpen(true);
        }}
      />
      <Dialog open={open} onOpenChange={(_, d) => setOpen(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete {msp.name}?</DialogTitle>
            <DialogContent>
              This permanently removes the MSP and its branding. Any user whose MSP override
              pointed here falls back to domain match / default branding. This cannot be undone.
              {err && <Text className={ls.blockMt8Danger}>{err}</Text>}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                className={ls.dangerFill}
                disabled={del.isPending}
                onClick={() => del.mutate()}
              >
                Delete MSP
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}

/* --------------------------------- edit ---------------------------------- */

function EditMspDialog({ msp, onClose, onSaved }: { msp: Msp; onClose: () => void; onSaved: () => void }) {
  const ls = useLocalStyles();
  const [name, setName] = useState(msp.name);
  const [domainsRaw, setDomainsRaw] = useState(msp.domains.join(', '));
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api(`/msps/${msp.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name, domains: splitDomains(domainsRaw) }),
      }),
    onSuccess: onSaved,
    onError: (e) => setErr(e instanceof Error ? e.message : 'Failed'),
  });

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Edit — {msp.name}</DialogTitle>
          <DialogContent>
            <div className={ls.gridGap12MinW380}>
              <Field label="Name">
                <Input value={name} onChange={(_, d) => setName(d.value)} />
              </Field>
              <Field label="Domains" hint="comma or newline separated, e.g. contoso.com, intl.contoso.com">
                <Input value={domainsRaw} onChange={(_, d) => setDomainsRaw(d.value)} />
              </Field>
              {err && <DangerText>{err}</DangerText>}
            </div>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={onClose}>
                Cancel
              </Button>
            </DialogTrigger>
            <Button appearance="primary" disabled={!name || save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

/* ------------------------------- branding ------------------------------- */

function BrandingDialog({ msp, onClose, onSaved }: { msp: Msp; onClose: () => void; onSaved: () => void }) {
  const ls = useLocalStyles();
  const [accentColor, setAccentColor] = useState(msp.branding?.accentColor ?? DEFAULT_ACCENT);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoRemoved, setLogoRemoved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      await api(`/msps/${msp.id}/branding`, { method: 'PATCH', body: JSON.stringify({ accentColor }) });
      if (logoFile) await apiUpload(`/msps/${msp.id}/branding/logo`, logoFile);
      else if (logoRemoved) await api(`/msps/${msp.id}/branding/logo`, { method: 'DELETE' });
    },
    onSuccess: onSaved,
    onError: (e) => setErr(e instanceof Error ? e.message : 'Failed'),
  });

  const previewUrl = logoRemoved
    ? null
    : logoFile
      ? URL.createObjectURL(logoFile)
      : msp.branding?.logo
        ? `/api/public/msps/${msp.id}/logo?v=${msp.branding.logo.version}`
        : null;

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Branding — {msp.name}</DialogTitle>
          <DialogContent>
            <div className={ls.gridGap12MinW380}>
              <div className={ls.flexItemsCenterGap12}>
                {previewUrl ? (
                  <img src={previewUrl} alt="" className={ls.wAutoH40} />
                ) : (
                  <Wordmark size={18} />
                )}
                <MutedText size={200}>
                  {previewUrl ? 'Current logo' : 'No logo set - default Voxshift mark shown everywhere'}
                </MutedText>
              </div>
              <Field label="Replace logo" hint="PNG or JPEG - WebP isn't supported by classic Outlook desktop">
                <div className={ls.flexItemsCenterGap8}>
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={(e) => {
                      setLogoFile(e.target.files?.[0] ?? null);
                      setLogoRemoved(false);
                    }}
                  />
                  {previewUrl && (
                    <Button
                      size="small"
                      appearance="subtle"
                      onClick={() => {
                        setLogoFile(null);
                        setLogoRemoved(true);
                      }}
                    >
                      Remove logo
                    </Button>
                  )}
                </div>
              </Field>
              <Field label="Accent color">
                <input
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className={ls.w40H32FilledPad0}
                />
              </Field>
              {err && <DangerText>{err}</DangerText>}
            </div>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={onClose}>
                Cancel
              </Button>
            </DialogTrigger>
            <Button appearance="primary" disabled={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

/** Connects the MSP's Teams channel (a Workflows webhook address, never shown again) and chooses what it is told. */
function TeamsDialog({ msp, onClose }: { msp: Msp; onClose: () => void }) {
  const ls = useLocalStyles();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['msp-teams', msp.id], queryFn: () => api<{ configured: boolean; notify: { incidents: boolean; major: boolean; sla: boolean } }>(`/msps/${msp.id}/teams`) });
  const [url, setUrl] = useState('');
  const [notify, setNotify] = useState<{ incidents: boolean; major: boolean; sla: boolean } | null>(null);
  const n = notify ?? q.data?.notify ?? { incidents: true, major: true, sla: true };
  const done = () => qc.invalidateQueries({ queryKey: ['msp-teams', msp.id] });
  const save = useMutation({
    mutationFn: () => api(`/msps/${msp.id}/teams`, { method: 'PATCH', body: JSON.stringify({ ...(url.trim() ? { webhookUrl: url.trim() } : {}), notify: n }) }),
    onSuccess: () => (setUrl(''), setNotify(null), done()),
  });
  const remove = useMutation({ mutationFn: () => api(`/msps/${msp.id}/teams`, { method: 'PATCH', body: JSON.stringify({ webhookUrl: null }) }), onSuccess: done });
  const test = useMutation({ mutationFn: () => api(`/msps/${msp.id}/teams/test`, { method: 'POST' }) });
  const error = save.error ?? remove.error ?? test.error;
  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface className={ls.w94vwMaxW640}>
        <DialogBody>
          <DialogTitle>Teams channel — {msp.name}</DialogTitle>
          <DialogContent className={ls.flexColRowGap12}>
            <Text size={200}>
              Tell a Teams channel about P1 and P2 incidents, major incidents and missed targets. In Teams, add a Workflow to the channel with the trigger &ldquo;When a Teams webhook request is received&rdquo; and
              paste its address here. The address is stored encrypted and is not shown again.
            </Text>
            <Text weight="semibold">{q.data?.configured ? 'A channel is connected.' : 'No channel connected.'}</Text>
            <Field label={q.data?.configured ? 'Replace the address (leave empty to keep it)' : 'Workflow address'}>
              <Input value={url} onChange={(_, d) => setUrl(d.value)} placeholder="https://…logic.azure.com/…" type="password" />
            </Field>
            {(['incidents', 'major', 'sla'] as const).map((k) => (
              <Checkbox key={k} checked={n[k]} onChange={(_, d) => setNotify({ ...n, [k]: !!d.checked })} label={k === 'incidents' ? 'A P1 or P2 incident is raised' : k === 'major' ? 'A major incident is declared or ends' : 'A response or resolution target is missed'} />
            ))}
            {error && <DangerText>{error instanceof Error ? error.message : 'That did not work.'}</DangerText>}
            {test.isSuccess && <Text>A test message is on its way: look in the channel in a few seconds.</Text>}
          </DialogContent>
          <DialogActions>
            {q.data?.configured && (
              <>
                <Button disabled={test.isPending} onClick={() => test.mutate()}>
                  Send a test
                </Button>
                <Button disabled={remove.isPending} onClick={() => window.confirm('Disconnect the channel?') && remove.mutate()}>
                  Disconnect
                </Button>
              </>
            )}
            <Button onClick={onClose}>Close</Button>
            <Button appearance="primary" disabled={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
